import { useEffect, useState } from "react";

interface UseApiOptions {
  enabled?: boolean;
  refetchInterval?: number;
  retryOnError?: boolean;
  maxRetries?: number;
  stopPollingWhen?: (data: any) => boolean; // Function to determine when to stop polling
}

interface UseApiReturn<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useApi<T>(
  apiCall: () => Promise<T>,
  options: UseApiOptions = {},
): UseApiReturn<T> {
  const {
    enabled = true,
    refetchInterval,
    retryOnError = false,
    maxRetries = 3,
    stopPollingWhen,
  } = options;

  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async (isInitialLoad = false, retryCount = 0) => {
    if (!enabled) return;

    try {
      // Only show loading spinner on initial load, not during polling
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      const result = await apiCall();
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");

      // Retry logic for 404 errors or if retryOnError is enabled
      if (
        retryOnError &&
        retryCount < maxRetries &&
        (error.message.includes("404") || error.message.includes("not found"))
      ) {
        console.log(
          `Retrying API call (attempt ${retryCount + 1}/${maxRetries})`,
        );
        setTimeout(
          () => fetchData(isInitialLoad, retryCount + 1),
          1000 * (retryCount + 1),
        ); // Exponential backoff
        return;
      }

      setError(error);
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData(true);
  }, [enabled]);

  useEffect(() => {
    if (refetchInterval && enabled) {
      // Check if we should stop polling
      if (stopPollingWhen && data && stopPollingWhen(data)) {
        return;
      }

      const interval = setInterval(fetchData, refetchInterval);
      return () => clearInterval(interval);
    }
  }, [refetchInterval, enabled, data, stopPollingWhen]);

  return {
    data,
    isLoading,
    error,
    refetch: () => fetchData(false),
  };
}
